<script setup lang="ts">
import { useAppState } from '~/composables/useAppState';
import Button from '~/components/atoms/Button.vue';
import TextInput from '~/components/ui/TextInput.vue';
import { countries } from '~/utils/countries';

const appState = useAppState();
const toast = useToast();

const submittingStatus = ref<boolean | null>(null);
const showEmailInput = ref(false);

const emailInput = ref('');
const emailError = ref('');

const nameInput = ref('');
const nameError = ref('');

const phoneCountry = ref('IN');
const phoneNumberInput = ref('');
const phoneError = ref('');

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isEmailValid = computed(() => EMAIL_REGEX.test(emailInput.value.trim()));

const isNameValid = computed(() => {
  const name = nameInput.value.trim();
  return name.length >= 2 && name.length <= 80 && /^[\p{L}\s'\-]+$/u.test(name);
});

const isPhoneValid = computed(() => {
  const cleaned = phoneNumberInput.value.replace(/\D/g, "");
  if (!cleaned) return true;
  if (phoneCountry.value === "IN") {
    return cleaned.length === 10 && /^[6-9]\d{9}$/.test(cleaned);
  } else {
    return cleaned.length >= 1 && cleaned.length <= 10;
  }
});

const isFormValid = computed(() => {
  if (!showEmailInput.value) return true;
  return isEmailValid.value && isNameValid.value && isPhoneValid.value;
});

function getMaxLength(iso: string): number {
  return 10;
}

function handlePhoneInput(e: Event) {
  const input = e.target as HTMLInputElement;
  let cleaned = input.value.replace(/\D/g, ""); // Keep digits only
  const max = getMaxLength(phoneCountry.value);
  if (cleaned.length > max) {
    cleaned = cleaned.substring(0, max);
  }
  phoneNumberInput.value = cleaned;
  input.value = cleaned;
}

watch(phoneCountry, (newCountry) => {
  if (phoneNumberInput.value) {
    const max = getMaxLength(newCountry);
    if (phoneNumberInput.value.length > max) {
      phoneNumberInput.value = phoneNumberInput.value.substring(0, max);
    }
  }
});

const validateNameOnBlur = () => {
  const name = nameInput.value.trim();
  if (!name) {
    nameError.value = 'Name is required';
  } else if (name.length < 2) {
    nameError.value = 'Name must be at least 2 characters';
  } else if (!/^[\p{L}\s'\-]+$/u.test(name)) {
    nameError.value = 'Name contains invalid characters';
  } else {
    nameError.value = '';
  }
};

const validateEmailOnBlur = () => {
  if (!emailInput.value.trim()) {
    emailError.value = 'Email is required';
  } else if (!isEmailValid.value) {
    emailError.value = 'Please enter a valid email address';
  } else {
    emailError.value = '';
  }
};

const validatePhoneOnBlur = () => {
  const cleaned = phoneNumberInput.value.replace(/\D/g, "");
  if (!cleaned) {
    phoneError.value = '';
  } else if (phoneCountry.value === 'IN') {
    if (cleaned.length !== 10 || !/^[6-9]\d{9}$/.test(cleaned)) {
      phoneError.value = 'Enter valid phone number';
    } else {
      phoneError.value = '';
    }
  } else {
    if (cleaned.length < 1 || cleaned.length > 10) {
      phoneError.value = 'Phone number must be up to 10 digits';
    } else {
      phoneError.value = '';
    }
  }
};

watch(() => appState.isRsvpModalOpen.value, (isOpen) => {
  if (isOpen) {
    nameInput.value = '';
    nameError.value = '';
    phoneCountry.value = 'IN';
    phoneNumberInput.value = '';
    phoneError.value = '';
    if (!appState.rsvpEmail.value) {
      showEmailInput.value = true;
      emailInput.value = '';
      emailError.value = '';
    } else {
      showEmailInput.value = false;
      emailInput.value = appState.rsvpEmail.value;
      emailError.value = '';
    }
  }
});

const submitRsvp = async (status: boolean) => {
  if (submittingStatus.value !== null) return;

  if (showEmailInput.value) {
    validateNameOnBlur();
    validateEmailOnBlur();
    validatePhoneOnBlur();
    if (nameError.value || emailError.value || phoneError.value) return;
    appState.rsvpEmail.value = emailInput.value.trim();
  }

  submittingStatus.value = status;
  try {
    let formattedPhone: string | null = null;
    if (showEmailInput.value && phoneNumberInput.value.trim().length > 0) {
      const selected = countries.find((c) => c.iso === phoneCountry.value);
      let cleaned = phoneNumberInput.value.replace(/\D/g, "");
      if (selected) {
        const prefixDigits = selected.dialCode.replace(/\D/g, "");
        if (cleaned.startsWith(prefixDigits) && cleaned.length > prefixDigits.length) {
          cleaned = cleaned.substring(prefixDigits.length);
        }
        formattedPhone = `${selected.dialCode}-${cleaned}`;
      } else {
        formattedPhone = `${phoneCountry.value}-${cleaned}`;
      }
    }

    await $fetch('/api/rsvp', {
      method: 'POST',
      body: { 
        email: appState.rsvpEmail.value,
        artworkId: appState.rsvpArtworkId.value,
        status: status,
        name: showEmailInput.value ? nameInput.value.trim() : null,
        phone: showEmailInput.value ? formattedPhone : null
      }
    });

    toast.add({
      title: status ? "Great! We’ll get in touch soon!" : "We'll miss you!",
      color: "success",
      duration: 3000,
    });
    
    appState.isRsvpModalOpen.value = false;
  } catch (error: any) {
    toast.add({
      title: error?.data?.message || "Failed to submit RSVP",
      color: "error",
      duration: 3000,
    });
  } finally {
    submittingStatus.value = null;
  }
};
</script>

<template>
  <UModal v-model:open="appState.isRsvpModalOpen.value" title="">
    <template #body>
      <div class="flex flex-col gap-6 p-4">
        <p class="text-base text-gray-700 text-start">
          Please share your contact details, and our team will get in touch with you shortly.
        </p>

        <div v-if="showEmailInput" class="flex flex-col gap-4">
          <!-- Name Field -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">Name <span class="text-red-500">*</span></label>
            <TextInput
              v-model="nameInput"
              type="text"
              placeholder="Your name"
              :error="!!nameError"
              @blur="validateNameOnBlur"
            />
            <p v-if="nameError" class="text-xs text-red-500">{{ nameError }}</p>
          </div>

          <!-- Email Field -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">Email Address <span class="text-red-500">*</span></label>
            <TextInput
              v-model="emailInput"
              type="email"
              placeholder="example@email.com"
              :error="!!emailError"
              @blur="validateEmailOnBlur"
            />
            <p v-if="emailError" class="text-xs text-red-500">{{ emailError }}</p>
          </div>

          <!-- Phone Number Field -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-gray-700">Phone Number <span class="text-gray-400 font-normal">(Optional)</span></label>
            <div class="flex items-stretch gap-2 w-full">
              <!-- Country code select -->
              <div class="w-[110px] shrink-0 relative flex">
                <select
                  v-model="phoneCountry"
                  class="w-full h-full appearance-none rounded-[6px] border-0 px-3 py-3 pr-8 text-sm md:text-base font-medium text-on-primary-heading ring ring-inset ring-on-primary-heading transition-colors outline-none focus-visible:ring-2 bg-white dark:bg-black"
                >
                  <option v-for="c in countries" :key="c.iso" :value="c.iso">
                    {{ c.flag }} {{ c.dialCode }}
                  </option>
                </select>
                <div class="absolute inset-y-0 right-3 flex items-center pointer-events-none text-on-primary-heading/60">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              <!-- Phone input -->
              <div class="flex-1">
                <TextInput
                  type="tel"
                  :placeholder="countries.find(c => c.iso === phoneCountry) ? countries.find(c => c.iso === phoneCountry)!.name + ' phone number' : 'Phone number'"
                  v-model="phoneNumberInput"
                  :maxlength="getMaxLength(phoneCountry)"
                  @input="handlePhoneInput"
                  :error="!!phoneError"
                  @blur="validatePhoneOnBlur"
                />
              </div>
            </div>
            <p v-if="phoneError" class="text-xs text-red-500">{{ phoneError }}</p>
          </div>
        </div>

        <div class="flex gap-4">
          <Button 
            variant="secondary" 
            class="flex-1 justify-center" 
            :loading="submittingStatus === false" 
            :disabled="submittingStatus === true || !isFormValid"
            @click="submitRsvp(false)"
          >
            No
          </Button>
          <Button 
            variant="primary" 
            class="flex-1 justify-center" 
            :loading="submittingStatus === true" 
            :disabled="submittingStatus === false || !isFormValid"
            @click="submitRsvp(true)"
          >
            Yes
          </Button>
        </div>
      </div>
    </template>
  </UModal>
</template>
